import { tripPlannerScopeKey } from '@luxuryescapes/contract-trip'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'
import {
  ASSISTANT_SURFACE_LABEL_MAX_LENGTH,
  sanitizeAssistantSurfacePlainText,
  toAssistantSurfaceId,
} from 'tripPlanner/assistant/surfaceSnapshots/assistantSurfacePlainText'
import type {
  AssistantSurfaceSnapshotChunk,
  AssistantSurfaceSnapshotChunkManifest,
  SurfaceCapabilityRequest,
  SurfaceManifestFactRequest,
  SurfaceVisibleStateRequest,
} from 'tripPlanner/assistant/api/surfaceSnapshotContract'
import type { AssistantSurfaceSnapshotBuildResult } from 'tripPlanner/assistant/surfaceSnapshots/useAssistantSurfaceSnapshot'
import type { TripPlannerSurfaceActivityEntry } from 'tripPlanner/assistant/surfaceSnapshots/surfaceActivityEntryTypes'
import type { TripPlannerCommittedStateEntry } from 'tripPlanner/assistant/surfaceSnapshots/committedStateEntry'
import { sortCommittedState } from 'tripPlanner/assistant/surfaceSnapshots/committedState'
import type { DrawerStackEntry } from 'tripPlanner/pages/chat/ChatTripPlannerPage/hooks/useDrawerStackState'
import type { CartItemPrice, TurnAuthoritySnapshot } from 'tripPlanner/pages/chat/ChatTripPlannerPage/contexts/TurnAuthoritySnapshotContext'
import { assistantSurfaceSnapshotChunkContentHash, buildAssistantSurfaceSnapshotChunkRef } from './fingerprint'

const capabilities: Array<SurfaceCapabilityRequest> = [{
  capability: 'surface_context.read',
  mode: 'available',
}]

// svc-trip validates activeDrawer as plainTextString(ASSISTANT_SURFACE_LABEL_MAX_LENGTH) --
// exceeding it, or including markdown/HTML/a custom-protocol-looking substring, 400s the whole
// ensure call, degrading the entire turn (read tool + every cart-price fact) precisely when a
// drawer is open. Composes required identity parts first, dropping optional parts from the tail
// (in order) if the result would exceed the cap, and omitting the whole descriptor (never
// truncating a part directly) only if even the required parts don't fit -- an id truncated
// mid-string would silently misdirect a lookup_offer/lookup_google_places call with nothing to
// catch it, which is worse than the model simply not seeing an active drawer this turn.
// '|' (not ':') is this file's delimiter for composing identity parts together, specifically so
// an id part is never ambiguous with the delimiter: toAssistantSurfaceId's safe charset
// deliberately allows ':' (some callers rely on it for their own already-meaningful compound
// ids, e.g. experience offerIds are `providerKey:externalId` -- see offerUtils.ts's
// detectOfferTypeFromId), so joining with ':' would make `offerDrawer:provider123:extRef456`
// read as *three* colon-delimited segments instead of two -- a model extracting "the offerId" to
// pass to lookup_offer could easily grab just `provider123` and silently drop `extRef456`. That
// failure is worse than the collision itself: it isn't a crash or a 400, it's a wrong tool-call
// argument with nothing to catch it. Collapsing the id's own colons instead (e.g. to '-') would
// avoid the ambiguity but corrupts the id itself, which is exactly what this file already
// forbids for truncation and shouldn't allow here either. Picking a delimiter outside the id
// charset sidesteps both: no id can ever contain '|', so no id part needs to change at all. Free
// text (locationInfo/placeSearchPending) isn't charset-restricted, so it could in principle still
// contain a literal '|', but that's a structural-ambiguity risk over free text with no id to
// protect, not an id-corruption risk -- and far less likely in practice than ':' was.
function composeIdentityDescriptor(requiredParts: Array<string>, optionalParts: Array<string>): string | undefined {
  for (let dropCount = 0; dropCount <= optionalParts.length; dropCount++) {
    const candidate = [...requiredParts, ...optionalParts.slice(0, optionalParts.length - dropCount)].join('|')
    if (candidate.length <= ASSISTANT_SURFACE_LABEL_MAX_LENGTH) {
      return candidate
    }
  }
  return undefined
}

// Per-variant identity, matching useDrawerStackState's own isSameDrawerTarget comparison
// exactly -- the entry's top-level `id` is a push-order counter, not a content identity, so
// it is never used here. Doubles as a cheap pointer into lookup_offer/lookup_google_places
// (both unconditionally available to trip_planner turns) for the two variants with a real
// place/offer id; locationInfo and placeSearchPending have no matching lookup tool, but the
// descriptive string still gives the model useful context.
//
// Two different sanitization strategies, not one blanket sanitizer over the whole composed
// string: detailPanel/placeInfo/offerDrawer embed a real id the model may read back out for a
// lookup call, so their ids are normalized to a safe charset via toAssistantSurfaceId (never
// truncated) and only the non-id parts are droppable under length pressure. locationInfo/
// placeSearchPending carry only free text (LLM-emitted titles/search queries, no length or
// charset guarantee) with no id to protect -- but they still go through composeIdentityDescriptor
// rather than being individually bounded then re-bounded as a whole: bounding `title` to the full
// 80-char budget on its own, then re-bounding the composed `locationInfo|${title}|${placeSearch}`
// string a second time, silently drops placeSearch entirely (not just shortens it) for any title
// past ~67 chars, since the kind prefix alone already leaves no room once title has already
// claimed the whole budget. Sanitizing (not bounding) each field and letting
// composeIdentityDescriptor own the one length/drop decision avoids that.
function describeDrawerEntry(entry: DrawerStackEntry): string | undefined {
  switch (entry.kind) {
    case 'detailPanel': {
      const placeId = toAssistantSurfaceId(entry.data.googlePlaceId)
      if (placeId === undefined) return undefined
      const stopId = entry.data.stopId !== undefined ? [String(entry.data.stopId)] : []
      return composeIdentityDescriptor(['detailPanel', placeId], stopId)
    }
    case 'placeInfo': {
      const placeId = toAssistantSurfaceId(entry.data.googlePlaceId)
      return placeId !== undefined ? composeIdentityDescriptor(['placeInfo', placeId], []) : undefined
    }
    case 'placeSearchPending': {
      const searchQuery = sanitizeAssistantSurfacePlainText(entry.data.searchQuery)
      return searchQuery !== undefined ? composeIdentityDescriptor(['placeSearchPending', searchQuery], []) : undefined
    }
    case 'locationInfo': {
      const title = sanitizeAssistantSurfacePlainText(entry.data.title)
      if (title === undefined) return undefined
      const placeSearch = sanitizeAssistantSurfacePlainText(entry.data.placeSearch)
      return composeIdentityDescriptor(['locationInfo', title], placeSearch !== undefined ? [placeSearch] : [])
    }
    case 'offerDrawer': {
      const offerId = toAssistantSurfaceId(entry.data.offerId)
      if (offerId === undefined) return undefined
      const offerType = entry.data.offerType ? [entry.data.offerType] : []
      const propertyId = toAssistantSurfaceId(entry.data.accommodationPropertyId)
      const optionalParts = propertyId !== undefined ? [...offerType, propertyId] : offerType
      return composeIdentityDescriptor(['offerDrawer', offerId], optionalParts)
    }
    default:
      return assertUnreachable(entry)
  }
}

// Hard technical ceiling, not a design choice: svc-trip rejects the entire snapshot publish
// (not just this chunk) if the total manifestFacts array exceeds
// ASSISTANT_SURFACE_MANIFEST_FACT_MAX_COUNT (80, assistantSurfaceProtocolSchemas.ts) --
// compactOptionalArrayWithStrictCap's overflow mode there is 'reject', not truncate. This is a
// separate constraint from svc-trip's render-time budget (which does truncate, and whose own
// fix belongs on the BE -- see docs/tripPlanner/svc-trip-handoff-be-work.md item 5). This margin
// exists purely so a large cart can never trigger that reject; it is deliberately generous, not
// a relevance/priority cap -- realistic carts stay far under it, and it only ever drops whole
// items (never splits one) in the rare case a cart approaches the platform ceiling.
const CART_MANIFEST_FACT_PUBLISH_SAFETY_MARGIN = 64

// Per-item facts, not one combined blob: displayedPrice reflects the List row's own LuxPlus
// preview toggle (any customer can flip it regardless of real membership), while
// checkoutPrice reflects real entitlement and matches what checkout will actually charge.
// These can genuinely differ for a non-member previewing member pricing -- sending only one
// and guessing which the model needs risks the exact "checkout price differs from the AI
// quote" bug this fact exists to prevent, just from a different cause. countsTowardTotal is
// explicit for the same reason: a customer-excluded or not-yet-bookable item still has a
// real price, but summing it into "what will this cost" would over-count -- letting the
// model infer eligibility from a fact's mere presence risks the same guessing failure mode.
// isBookingReady guards a third failure mode on the same theme: an item not yet fully
// configured in an active cart entry (dates/room/occupancy all selected) only has an
// indicative starting price (e.g. "from $250/night"), expected to change once the customer
// finalises their pick -- matching TripListRowPrice's own "From" prefix. Without this, the
// model has no way to tell a locked-in price from one that's still expected to move, and can
// over-confidently quote an indicative number as if it were final. checkoutBlocker is
// omitted (not sent as "none"/"ready") when the item has no blocker -- unlike price/
// eligibility, every item always has *a* value for those, but "no blocker" is genuinely the
// common case and not worth a fact of its own.
function buildCartItemManifestFacts({ itemId, displayedPrice, checkoutPrice, countsTowardTotal, isBookingReady, checkoutBlocker }: CartItemPrice): Array<SurfaceManifestFactRequest> {
  return [
    {
      key: `cart_displayed_price:${itemId}`,
      value: displayedPrice,
      dataClassification: 'commercial_surface',
    },
    {
      key: `cart_checkout_price:${itemId}`,
      value: checkoutPrice,
      dataClassification: 'commercial_surface',
    },
    {
      key: `cart_counts_toward_total:${itemId}`,
      value: countsTowardTotal ? 'true' : 'false',
      dataClassification: 'commercial_surface',
    },
    {
      key: `cart_is_booking_ready:${itemId}`,
      value: isBookingReady ? 'true' : 'false',
      dataClassification: 'commercial_surface',
    },
    ...(checkoutBlocker !== undefined ? [{
      key: `cart_checkout_blocker:${itemId}`,
      value: checkoutBlocker,
      dataClassification: 'commercial_surface' as const,
    }] : []),
  ]
}

function buildVisibleState(turnAuthoritySnapshot: TurnAuthoritySnapshot): SurfaceVisibleStateRequest {
  const manifestFacts: Array<SurfaceManifestFactRequest> = []
  for (const cartItemPrice of turnAuthoritySnapshot.cartItemPrices) {
    const itemFacts = buildCartItemManifestFacts(cartItemPrice)
    if (manifestFacts.length + itemFacts.length > CART_MANIFEST_FACT_PUBLISH_SAFETY_MARGIN) {
      break
    }
    manifestFacts.push(...itemFacts)
  }

  const activeDrawer = turnAuthoritySnapshot.isAnyDrawerOpen && turnAuthoritySnapshot.topEntry
    ? describeDrawerEntry(turnAuthoritySnapshot.topEntry)
    : undefined

  return {
    ...(turnAuthoritySnapshot.rightPanelView ? { primaryMode: turnAuthoritySnapshot.rightPanelView } : {}),
    ...(activeDrawer !== undefined ? { activeDrawer } : {}),
    ...(manifestFacts.length > 0 ? { manifestFacts } : {}),
  }
}

// No count cap here: useSurfaceActivityAccumulator's capAndTrackDrops already enforces the
// 40-entry cap (matching the backend's own ASSISTANT_SURFACE_ACTIVITY_MAX_COUNT) on every path
// that can grow its buffer -- push and restore alike -- so drainSurfaceActivity()'s return value,
// this function's only real input, can never exceed it. Re-capping here would duplicate that
// invariant in a second place instead of trusting the one place it's actually enforced.
//
// reverse() gives newest-first on the wire. Presentational, not load-bearing -- the backend
// selects on occurredAt regardless of wire order -- but kept because it makes a captured request
// read the way the model receives it. No sort function needed here either: push order IS
// occurredAt order by the time entries reach this point, because restoreDrainedActivity is the
// one place that invariant could break under overlapping restores, and it re-sorts there rather
// than leaving every downstream consumer (including this one) to distrust push order defensively.
function buildRecentSurfaceActivity(entries: Array<TripPlannerSurfaceActivityEntry>): Array<TripPlannerSurfaceActivityEntry> {
  return [...entries].reverse()
}

function buildCommittedStateChunk(recentCommittedState: Array<TripPlannerCommittedStateEntry>): AssistantSurfaceSnapshotChunk {
  return {
    chunkId: 'committed',
    kind: 'recent_committed_state',
    contentHash: assistantSurfaceSnapshotChunkContentHash({ kind: 'recent_committed_state', recentCommittedState }),
    recentCommittedState,
  }
}

function buildCapabilityChunk(): AssistantSurfaceSnapshotChunk {
  return {
    chunkId: 'trip_planner',
    kind: 'capabilities',
    contentHash: assistantSurfaceSnapshotChunkContentHash({ kind: 'capabilities', capabilities }),
    capabilities,
  }
}

export interface TripPlannerSnapshotInput {
  chatId: number
  turnAuthoritySnapshot: TurnAuthoritySnapshot
  surfaceActivityEntries: Array<TripPlannerSurfaceActivityEntry>
  committedStateEntries: Array<TripPlannerCommittedStateEntry>
}

export function buildTripPlannerSnapshot(input: TripPlannerSnapshotInput): AssistantSurfaceSnapshotBuildResult {
  const surfaceContextScopeKey = tripPlannerScopeKey({ chatId: input.chatId })
  const visibleState = buildVisibleState(input.turnAuthoritySnapshot)
  const recentCommittedState = sortCommittedState(input.committedStateEntries)
  const chunks = [buildCommittedStateChunk(recentCommittedState), buildCapabilityChunk()]
  const recentSurfaceActivity = buildRecentSurfaceActivity(input.surfaceActivityEntries)

  const manifest: AssistantSurfaceSnapshotChunkManifest = {
    surface: 'trip_planner',
    schemaVersion: 1,
    surfaceContextScopeKey,
    visibleState,
    ...(recentSurfaceActivity.length > 0 ? { recentSurfaceActivity } : {}),
    chunks: chunks.map(buildAssistantSurfaceSnapshotChunkRef),
  }

  return {
    chunks,
    manifest,
    snapshot: {
      surface: 'trip_planner',
      schemaVersion: 1,
      surfaceContextScopeKey,
      visibleState,
      ...(recentSurfaceActivity.length > 0 ? { recentSurfaceActivity } : {}),
      atoms: [],
      targets: [],
      recentCommittedState,
      capabilities,
    },
  }
}
